/**
 * Below-canvas, lower-right tray surfacing every USABLE power-up the
 * pilot has stockpiled, with a clear count badge per tile.
 *
 * Tile types:
 *   - Worm Swarm  — tappable, fires `onLaunchWormSwarm()` per tap and
 *                   consumes one charge from `walletCfg.pendingStarters`.
 *   - Mass Extinction variant fires gold worms (Worm Tamer achievement).
 *   - Deferred starter (e.g. Wave-5 Plasma) — non-tappable display
 *                   showing "READY @ WAVE N" until auto-fired.
 *
 * Extracted from `GameCanvas.jsx` on 2026-05-13. The lower-right
 * placement was a direct UX ask — keeps the tray off the gameplay
 * surface but right next to where the pilot's thumb naturally rests
 * on mobile (the canvas's lower-right corner).
 */

// Static config for deferred starters — kept in sync with the
// DEFERRED_STARTERS map in GameCanvas. Re-declared here so the tray
// can render the wave/colour/label without re-importing GameCanvas
// constants (the canvas itself is already huge).
const DEFERRED_TILE_CFG = {
  "wave-5-plasma": { wave: 5, color: "#ff66ff", label: "Plasma Boost" },
  "wave-10-comet": { wave: 10, color: "#ff8c42", label: "Comet Storm" },
};

export function PowerupTray({
  walletCfg,
  balance,
  currentWave,
  endless,
  paused,
  gameOver,
  won,
  startersUsed,
  onLaunchWormSwarm,
  onEarlyArm,
  earlyArmCost = 25,
}) {
  if (paused || gameOver || won) return null;
  const pending = walletCfg?.pendingStarters || [];
  const wormCount = pending.filter((s) => s === "worm-swarm").length;
  const used = startersUsed || [];
  const deferred = endless
    ? Object.entries(DEFERRED_TILE_CFG)
        .filter(([sid]) => pending.includes(sid) && !used.includes(sid))
        .map(([sid, cfg]) => ({ sid, ...cfg }))
    : [];
  if (wormCount === 0 && deferred.length === 0) return null;

  const isMassEx = !!walletCfg?.wormTamerUnlocked;
  const wormAccent = isMassEx ? "#ffea3a" : "#00ff66";
  const wormGlow = isMassEx ? "rgba(255,234,58,0.55)" : "rgba(0,255,102,0.55)";

  return (
    <div
      className="w-full max-w-[800px] mt-3 flex flex-wrap items-stretch justify-end gap-2"
      data-testid="powerup-tray"
    >
      <span
        className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/45 self-center mr-auto"
        data-testid="powerup-tray-label"
      >
        // Power-ups · {wormCount + deferred.length} ready
      </span>

      {wormCount > 0 && (
        <button
          type="button"
          onClick={onLaunchWormSwarm}
          className="px-3 py-2 border bg-black/70 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase flex items-center gap-2 transition active:scale-95 hover:bg-white/[0.04]"
          style={{
            borderColor: wormAccent,
            color: wormAccent,
            boxShadow: `0 0 12px ${wormGlow}`,
          }}
          data-testid="powerup-tray-worm-swarm"
          data-mass-ex={isMassEx ? "1" : "0"}
          title={
            isMassEx
              ? "Mass Extinction · Launch a swarm of golden worms"
              : "Launch a swarm of tiny green worms — they hunt the nearest brick"
          }
        >
          <span className="text-[13px] leading-none">~~</span>
          <span>{isMassEx ? "Mass Extinction" : "Launch Worms"}</span>
          <span
            className="px-1.5 py-0.5 font-heading text-[9px] tabular-nums leading-none text-black"
            style={{ background: wormAccent }}
            data-testid="powerup-tray-worm-count"
          >
            ×{wormCount}
          </span>
        </button>
      )}

      {deferred.map((d) => {
        const imminent = d.wave - currentWave === 1;
        const ready = currentWave >= d.wave;
        // Early-arm — pilot spends `earlyArmCost` ☾ to fire the starter
        // immediately instead of waiting for its scheduled wave. The
        // `balance` prop is the canonical wallet number from GameCanvas
        // (separate from walletCfg.pendingStarters); falls back to
        // walletCfg.balance only as a defensive secondary read.
        const liveBalance = Number(
          balance ?? walletCfg?.balance ?? 0,
        );
        const canAfford = liveBalance >= earlyArmCost;
        const armable = !ready && !!onEarlyArm;
        const handleClick = armable && canAfford
          ? () => onEarlyArm(d.sid)
          : undefined;
        const isInteractive = armable && canAfford;
        const Tag = isInteractive ? "button" : "div";
        const tagProps = isInteractive
          ? { type: "button", onClick: handleClick }
          : {};
        return (
          <Tag
            key={d.sid}
            {...tagProps}
            className={`px-3 py-2 border bg-black/55 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase flex items-center gap-2 ${
              imminent ? "animate-pulse" : ""
            } ${isInteractive ? "transition active:scale-95 hover:bg-white/[0.04] cursor-pointer" : ""} ${
              armable && !canAfford ? "opacity-55" : ""
            }`}
            style={{
              borderColor: `${d.color}aa`,
              color: d.color,
              boxShadow: imminent
                ? `0 0 14px ${d.color}aa`
                : `0 0 8px ${d.color}55`,
            }}
            data-testid={`powerup-tray-${d.sid}`}
            data-early-arm={armable ? "1" : "0"}
            title={
              armable
                ? canAfford
                  ? `Spend ${earlyArmCost} ☾ to arm now · auto-fires every ${d.wave} waves`
                  : `Need ${earlyArmCost} ☾ to arm now (you have ${liveBalance})`
                : `Auto-fires on every ${d.wave}th wave (boosted)`
            }
          >
            <span>{d.label}</span>
            <span
              className="px-1.5 py-0.5 font-heading text-[9px] tabular-nums leading-none text-black"
              style={{ background: d.color }}
              data-testid={`powerup-tray-${d.sid}-status`}
            >
              {ready ? "NOW" : `W${d.wave}`}
            </span>
            {armable && (
              <span
                className={`px-1.5 py-0.5 font-mono-cosmic text-[9px] tabular-nums leading-none border ${
                  canAfford ? "border-white/40 text-white/80" : "border-white/20 text-white/40"
                }`}
                data-testid={`powerup-tray-${d.sid}-early-arm`}
              >
                {earlyArmCost} ☾
              </span>
            )}
          </Tag>
        );
      })}
    </div>
  );
}
